
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CompanyProfile {
  id: string;
  name: string;
  slug: string;
  active: boolean;
}

export const useCompanyProfile = () => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        // Get current user directly from supabase
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setCompanyProfile(null);
          setLoading(false);
          return;
        }

        // Get user's active company from user_company_associations
        const { data: association, error: associationError } = await supabase
          .from('user_company_associations')
          .select('company_id, companies!inner(id, name, slug, active)')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (associationError) {
          console.error('Error fetching user company association:', associationError);
          setCompanyProfile(null);
          setLoading(false);
          return;
        }

        if (!association?.companies) {
          setCompanyProfile(null);
          setLoading(false);
          return;
        }

        // Extract company from association
        const company = association.companies;

        setCompanyProfile(company);
      } catch (error) {
        console.error('Error in fetchCompanyProfile:', error);
        setCompanyProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyProfile();

    // Listen for auth state changes to refetch company profile
    // Note: TOKEN_REFRESHED is excluded - it fires on tab focus and doesn't change company data
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        fetchCompanyProfile();
      } else if (event === 'SIGNED_OUT') {
        setCompanyProfile(null);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { companyProfile, loading };
};
